import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { animationFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
import { patternFields } from '../_shared/pattern.js';
/**
 * collage — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "Multimedia collage" section
 * (shrine-pro-ref/sections/collage.liquid + assets/collage.css). The collage is the distinctive
 * grid where the FIRST block is a large tile (spanning 2×2 on desktop) and the remaining blocks stack
 * beside it — mixing an IMAGE tile, a PRODUCT card, a COLLECTION card and (aquilixShop) an uploaded
 * VIDEO tile. Max 3 blocks, exactly as Shrine.
 *
 * Field-type mapping (Shopify → ours): inline_richtext→text (with bold / italic markup), image_picker→image,
 * product→product-picker, collection→collection-picker, video_url(YouTube/Vimeo)→our uploaded `video`
 * field (aquilixShop rule: media is uploaded, never a raw URL — see spec 0020), color→color, header→group,
 * `visible_if`→our `visibleWhen`. Ids are clean camelCase; labels/options/defaults stay faithful to Shrine.
 *
 * The word-HIGHLIGHT engine (8 styles on bold / italic words) and the 13-pattern BACKGROUND engine are
 * the shared factories every other rebuilt section uses, so the collage's inspector matches them exactly.
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const GENERAL = 'General';
export const collageSchema = {
    type: 'collage',
    name: 'Multimedia collage',
    version: 1,
    templates: ['index', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            default: false,
            info: 'Lets a button link scroll to this section.',
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── General: heading + highlight + size ─────────────────────────────────────
        {
            id: 'heading',
            type: 'text',
            label: 'Heading',
            group: GENERAL,
            default: 'Multimedia collage',
            defaultKey: 'section.collage',
            info: 'Wrap words in ** ** for highlight 1, * * for highlight 2.',
        },
        ...headingHighlightFields(1, GENERAL),
        ...headingHighlightFields(2, GENERAL),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: GENERAL,
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'headingSizeDesktop',
            type: 'range',
            label: 'Custom desktop size',
            group: GENERAL,
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'headingSizeMobile',
            type: 'range',
            label: 'Custom mobile size',
            group: GENERAL,
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        // ── Layout ──────────────────────────────────────────────────────────────────
        {
            id: 'desktopLayout',
            type: 'select',
            label: 'Desktop layout',
            group: 'Layout',
            default: 'left',
            options: [
                { value: 'left', label: 'Left large block' },
                { value: 'right', label: 'Right large block' },
            ],
        },
        {
            id: 'mobileLayout',
            type: 'select',
            label: 'Mobile layout',
            group: 'Layout',
            default: 'column',
            options: [
                { value: 'collage', label: 'Collage' },
                { value: 'column', label: 'Column' },
            ],
        },
        {
            id: 'cardStyles',
            type: 'select',
            label: 'Image and video card styles',
            group: 'Layout',
            default: 'product-card-wrapper',
            info: 'Applies the product-card border, corner radius and shadow to the image and video tiles.',
            options: [
                { value: 'none', label: 'None' },
                { value: 'product-card-wrapper', label: 'Product card' },
            ],
        },
        // ── General: color scheme + custom palette ──────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: GENERAL,
            default: 'background-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customBackground',
            type: 'color',
            label: 'Custom background color',
            group: GENERAL,
            default: hex('#FFFFFF'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: GENERAL,
            info: 'If set, replaces the background color.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: GENERAL,
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: GENERAL,
            info: 'If empty, the desktop image is used on mobile too.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customText',
            type: 'color',
            label: 'Custom text color',
            group: GENERAL,
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── General: background pattern (shared engine) ─────────────────────────────
        ...patternFields(GENERAL),
        mediaRadiusField,
        // ── Section padding ─────────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    // Inline blocks — addable only inside the collage (Shrine's collage blocks). Max 3.
    blocks: [
        {
            type: 'image',
            name: 'Image',
            fields: [{ id: 'image', type: 'image', label: 'Image' }],
        },
        {
            type: 'product',
            name: 'Product',
            fields: [
                { id: 'product', type: 'product-picker', label: 'Product' },
                {
                    id: 'secondImage',
                    type: 'checkbox',
                    label: 'Show second image on hover',
                    default: false,
                },
            ],
        },
        {
            type: 'collection',
            name: 'Collection',
            fields: [
                { id: 'collection', type: 'collection-picker', label: 'Collection' },
            ],
        },
        {
            type: 'video',
            name: 'Video',
            fields: [
                { id: 'coverImage', type: 'image', label: 'Cover image' },
                { id: 'video', type: 'video', label: 'Video' },
                playButtonField({ defaultOn: false, visibleWhen: { field: 'video', empty: false } }),
                {
                    id: 'loop',
                    type: 'checkbox',
                    label: 'Video looping',
                    default: true,
                    info: 'Restarts the clip as soon as it ends, instead of stopping on the last frame.',
                },
                {
                    id: 'description',
                    type: 'text',
                    label: 'Video alt text',
                    default: 'Describe the video',
                    defaultKey: 'section.describe_video',
                    info: 'Describes the video for customers using assistive technology.',
                },
            ],
        },
    ],
    maxBlocks: 3,
};
export const collagePresets = [
    {
        name: 'Multimedia collage',
        settings: {
            headingSize: 'h1',
            desktopLayout: 'left',
            mobileLayout: 'column',
            cardStyles: 'product-card-wrapper',
            colorScheme: 'background-1',
            paddingTop: 36,
            paddingBottom: 36,
        },
        blocks: [{ type: 'image' }, { type: 'product' }, { type: 'collection' }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const collageMeta = {
    label: 'Multimedia collage',
    group: 'Content',
    icon: 'grid',
};
//# sourceMappingURL=schema.js.map